import { MS_PER_DAY, RECENT_PERIOD_MAPPINGS } from "../constants/date";
import type { RecentPeriod } from "../types/date";

/**
 * Formats a date as "DayName Day Month Year" (e.g., "Sun 1 Jan 2025")
 * or "DayName Day Month" (e.g., "Sun 1 Jan") if includeYear is false
 */
const formatDate = (date: Date, includeYear: boolean = true): string => {
  const dayFormatter = new Intl.DateTimeFormat("en-GB", { weekday: "short" });
  const dateFormatter = new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    ...(includeYear && { year: "numeric" }),
  });

  const dayName = dayFormatter.format(date);
  const formattedDate = dateFormatter.format(date);

  return `${dayName} ${formattedDate}`;
};

/**
 * Formats a date range. If both dates are in the same year, only shows the year at the end.
 * Same year: "Sun 1 Jan - Mon 2 Jan 2025"
 * Different years: "Sun 31 Dec 2024 - Mon 1 Jan 2025"
 */
export const formatDateRange = (startDate: Date, endDate?: Date): string => {
  const sameYear = endDate && startDate.getFullYear() === endDate.getFullYear();
  return `${formatDate(startDate, !sameYear)} - ${endDate ? formatDate(endDate, true) : ""}`;
};

/**
 * Converts a RecentPeriod to the number of days it represents.
 * @example recentPeriodToNoOfDays("LAST_7") // returns 7
 */
export const recentPeriodToNoOfDays = (recentPeriod: RecentPeriod): number => {
  return RECENT_PERIOD_MAPPINGS.find((m) => m.recentPeriod === recentPeriod)?.noOfDays ?? 0;
};

/**
 * Converts a number of days to its corresponding RecentPeriod.
 * @example noOfDaysToRecentPeriod(7) // returns "LAST_7"
 * @returns The RecentPeriod or undefined if no match exists
 */
export const noOfDaysToRecentPeriod = (noOfDays: number): RecentPeriod | undefined => {
  return RECENT_PERIOD_MAPPINGS.find((m) => m.noOfDays === noOfDays)?.recentPeriod;
};

/**
 * Returns a new date with the specified day offset applied.
 * @param date The base date
 * @param offset Number of days to offset (negative for past, positive for future)
 * @example getDateWithOffset(new Date("2025-01-15"), -1) // returns Jan 14, 2025
 * @example getDateWithOffset(new Date("2025-01-15"), 7) // returns Jan 22, 2025
 */
export const getDateWithOffset = (date: Date, offset: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + offset);
  return result;
};

/**
 * Normalizes a date to midnight (00:00:00.000) in local time.
 * This ensures consistent day-based calculations that aren't affected by DST transitions.
 * @param date The date to normalize
 * @returns A new date set to midnight of the same day
 * @example normalizeDate(new Date("2025-01-15T14:30:00")) // returns Jan 15, 2025 00:00:00
 */
export const normalizeDate = (date: Date): Date => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

/**
 * Formats a date as `YYYY-MM-DDT00:00:00.000Z` using its local calendar components.
 * Use this when sending dates to APIs that expect a calendar day as an ISO-8601 Instant,
 * to avoid timezone shifts caused by `Date#toISOString` (which converts to UTC).
 * @example formatLocalDate(new Date(2026, 3, 17)) // "2026-04-17T00:00:00.000Z"
 */
export const formatLocalDate = (date: Date): string => {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())).toISOString();
};

/**
 * Calculates the number of days between two dates (inclusive).
 * Normalizes dates to midnight before calculation to avoid DST issues.
 * @param startDate The start date
 * @param endDate The end date
 * @returns Number of days between the dates (inclusive), always >= 1 if endDate >= startDate
 * @example getDaysBetween(new Date("2025-01-15"), new Date("2025-01-21")) // returns 7
 * @example getDaysBetween(new Date("2025-01-15T23:00"), new Date("2025-01-15T01:00")) // returns 1 (same day)
 */
export const getDaysBetween = (startDate: Date, endDate: Date): number => {
  const normalizedStart = normalizeDate(startDate);
  const normalizedEnd = normalizeDate(endDate);

  const diffInMs = normalizedEnd.getTime() - normalizedStart.getTime();
  const diffInDays = Math.round(diffInMs / MS_PER_DAY);

  return diffInDays + 1; // +1 to make it inclusive
};

/**
 * Checks if two dates are the same (ignoring time).
 * @param date1 First date to compare
 * @param date2 Second date to compare
 * @returns true if both dates represent the same day, false otherwise
 * @example isSameDate(new Date("2025-01-15T10:00"), new Date("2025-01-15T22:00")) // returns true
 */
export const isSameDate = (date1: Date, date2: Date): boolean => {
  return normalizeDate(date1).getTime() === normalizeDate(date2).getTime();
};
